import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleLogin, GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { TestTube2, User, Lock, AlertCircle, ShieldCheck, Mail, Eye, EyeOff,
         ArrowRight, FileText, BarChart3, Shield } from 'lucide-react';
import Button from '../../components/common/Button';
import api from '../../services/api';
import axios from 'axios';
import { publicTenantHeaders } from '../../utils/publicTenant';
import './LoginPage.css';

// ---------------------------------------------------------------------------
// 2FA Code Input - 6 individual digit boxes with auto-advance
// ---------------------------------------------------------------------------
const TwoFactorCodeInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);

  const handleChange = (index, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const newValue = value.split('');
    newValue[index] = digit;
    const joined = newValue.join('');
    onChange(joined);

    // Auto-focus next box
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, ' ').slice(0, 6).replace(/ /g, ''));
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <div className="tfa-code-inputs" onPaste={handlePaste}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          className="tfa-digit-box"
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------


// The patient-portal OAuth client, used for the sign-in BUTTON on this page.
//
// Its project declares only openid/email/profile, so it shows no "unverified
// app" warning. The staff project cannot: its Gmail scopes put it in
// permanent verification. The ID token proves the same thing either way —
// Google asserting this email — and /auth/google now accepts both audiences,
// so staff signing in through the clean button are not turned away.
const SIGNIN_GOOGLE_CLIENT_ID =
  process.env.REACT_APP_PORTAL_GOOGLE_CLIENT_ID
  || process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Portal first, then staff — the same order and the same reason as the
// password path: a failed portal attempt has no side effects.
async function universalGoogle(credential) {
  const base = process.env.REACT_APP_API_URL || '';
  const org = new URLSearchParams(window.location.search).get('org') || '';
  const res = await fetch(`${base}/api/portal/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(org ? { 'X-Company-Id': org } : {}) },
    body: JSON.stringify({ credential }),
  });
  if (res.ok) return { kind: 'patient', data: await res.json() };
  return { kind: 'staff' };
}

// Straight fetch rather than the shared axios client: that client attaches the
// staff Authorization header and has a 401 interceptor that would read a
// wrong-password answer here as an expired staff session and bounce the page.
async function portalSignIn(email, password) {
  const org = new URLSearchParams(window.location.search).get('org') || '';
  const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/portal/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(org ? { 'X-Company-Id': org } : {}),
    },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error('not a patient account');
  return res.json();
}

/* A headline may pick one word out in the accent colour. Only <span>, <b>,
 * <strong>, <em> and <i> survive, and every attribute is dropped — a tenant
 * setting is admin-authored, but it is still not a place to put markup the
 * page has to trust. */
function safeHeadline(v) {
  return String(v || '')
    .replace(/<(?!\/?(?:span|b|strong|em|i)\b)[^>]*>/gi, '')
    .replace(/<(span|b|strong|em|i)\b[^>]*>/gi, '<$1>');
}

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() => {
    // The API sends people here with ?session=ended when the server has
    // finished their session — an admin changed the account, or the token
    // names nobody any more. Saying so beats a login form that appears for
    // no stated reason in the middle of somebody's work.
    try {
      const r = new URLSearchParams(window.location.search).get('session');
      return r === 'ended'
        ? 'Your session ended because this account was changed. Please sign in again.'
        : '';
    } catch { return ''; }
  });
  // The laboratory's own name and mark, so signing in does not jump from a
  // branded website to somebody else's product name.
  const [brand, setBrand] = useState({
    name: 'Enterprise LIS', tagline: 'Laboratory Information System', logo: null,
    // Everything below is the tenant's own and is simply not drawn when unset,
    // so a laboratory that has configured nothing still gets a complete page.
    headline: '', sub: '', photo: '', eyebrow: '', strapline: '',
    logoInvert: false });
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL
      || (window.location.protocol === 'https:'
            ? `${window.location.protocol}//${window.location.host}`
            : `http://${window.location.hostname}:8000`);
    axios.get(`${API}/api/cms/settings`, { headers: publicTenantHeaders() })
      .then(r => {
        const d = r.data || {};
        setBrand(b => ({
          name: d.site_name || b.name,
          tagline: d.tagline || b.tagline,
          // This page is always dark, so a dark-mode mark is preferred when
          // the tenant has one. `footer_logo_invert` already means "our mark
          // is dark and needs whitening on a dark ground" — the same fact,
          // so it is reused rather than asked for twice.
          logo: d.logo_url_dark || d.logo_url || null,
          logoInvert: !d.logo_url_dark
            && ['1', 'true', 'yes', 'on'].includes(
                 String(d.footer_logo_invert || '').trim().toLowerCase()),
          headline: d.login_headline || '',
          sub: d.login_sub || d.tagline || '',
          photo: d.login_bg_url || '',
          eyebrow: d.login_eyebrow || '',
          strapline: d.login_strapline || '',
        }));
      })
      .catch(() => { /* unbranded tenant keeps the default */ });
  }, []);
  const [isLoading, setIsLoading] = useState(false);

  // Forgot password flow
  const [showForgot, setShowForgot] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tfaUserId, setTfaUserId] = useState(null);
  const [tfaCode, setTfaCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // ── Apple Sign-In: load Apple's JS SDK once + init ───────────────────────
  const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || '';
  const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || `${window.location.origin}/auth/apple/callback`;
  const appleReady = useRef(false);

  useEffect(() => {
    if (!APPLE_SERVICES_ID) return;
    // Don't load twice
    if (document.querySelector('script[data-apple-signin]')) {
      appleReady.current = true;
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-apple-signin', 'true');
    s.onload = () => {
      try {
        if (window.AppleID) {
          window.AppleID.auth.init({
            clientId: APPLE_SERVICES_ID,
            scope: 'name email',
            redirectURI: APPLE_REDIRECT_URI,
            usePopup: true,
          });
          appleReady.current = true;
        }
      } catch (e) {
        console.error('Apple Sign-In init failed:', e);
      }
    };
    document.head.appendChild(s);
  }, [APPLE_SERVICES_ID, APPLE_REDIRECT_URI]);

  const handleAppleSignIn = async () => {
    if (!APPLE_SERVICES_ID) {
      setError('Apple Sign-In is not configured on this environment.');
      return;
    }
    if (!appleReady.current || !window.AppleID) {
      setError('Apple Sign-In is still loading — try again in a moment.');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      const data = await window.AppleID.auth.signIn();
      // data.authorization.id_token is the identity JWT
      // data.user is present ONLY on the first sign-in ({name: {firstName, lastName}, email})
      const idToken = data?.authorization?.id_token;
      if (!idToken) {
        throw new Error('Apple did not return an identity token');
      }
      const resp = await api.post('/auth/apple', {
        identity_token: idToken,
        user: data?.user || null,
      });
      const { access_token, refresh_token } = resp.data;
      await onLogin(access_token, refresh_token);
    } catch (err) {
      // Apple throws with {error: 'popup_closed_by_user', ...} on cancellation — don't show an error for that.
      if (err?.error === 'popup_closed_by_user') {
        setError('');
      } else {
        console.error('Apple Sign-In error:', err);
        setError(err?.response?.data?.detail || err?.message || 'Apple Sign-In failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // ONE DOOR.
      //
      // The patient portal is tried FIRST, and deliberately so: a failed
      // portal sign-in has no side effects — it increments no counter and
      // locks nothing — whereas the staff sign-in records failed attempts
      // against a username+IP throttle. Trying staff first would charge a
      // patient's password attempt to a staff account that may not even be
      // theirs; this way round costs nothing.
      //
      // Patients therefore always land in the portal, whichever table their
      // credentials happen to live in, and staff are unaffected.
      //
      // Deliberately NOT a new unified endpoint on the server: a second
      // authentication path is a second place for lockout, 2FA and token
      // issuance to drift out of step with the first.
      if (username.includes('@')) {
        try {
          const r = await portalSignIn(username, password);
          if (r?.access_token) {
            sessionStorage.setItem('portal_token', r.access_token);
            window.location.assign('/portal');
            return;
          }
        } catch {
          // Not a patient account, or wrong password for one. Fall through to
          // staff and let that side decide — the failure is not disclosed
          // either way.
        }
      }

      const loginResponse = await api.post('/auth/login', { username, password });
      const data = loginResponse.data;

      if (data.requires_2fa) {
        // Switch to 2FA verification step
        setNeeds2FA(true);
        setTfaUserId(data.user_id);
        setResendCountdown(60);
        setIsLoading(false);
        return;
      }

      // No 2FA required -- pass the token directly
      await onLogin(data.access_token, data.refresh_token);
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        setError(
          err.response.data?.detail ||
          `Login failed: ${err.response.status} ${err.response.statusText}`
        );
      } else if (err.request) {
        setError('No response from backend. Check network or CORS.');
      } else {
        setError('Login error: ' + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (tfaCode.length < 6) {
      setError('Please enter the full 6-digit code');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const res = await api.post('/auth/2fa/verify', {
        user_id: tfaUserId,
        code: tfaCode,
      });

      const data = res.data;
      if (data.verified && data.access_token) {
        // Complete login with the token from 2FA verification
        const { setAuthToken } = await import('../../services/api');
        setAuthToken(data.access_token, data.refresh_token);

        // Fetch user profile and finish
        const { authAPI } = await import('../../services/api');
        // Trigger the parent onLogin which expects token to be set
        // We call the low-level flow: set token then fetch /users/me
        window.location.reload();
      }
    } catch (err) {
      console.error('2FA verify error:', err);
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    setError('');

    try {
      // Re-submit login to trigger a new code send
      await api.post('/auth/login', { username, password });
      setResendCountdown(60);
    } catch {
      setError('Failed to resend code. Please try again.');
    }
  };

  const handleBack = () => {
    setNeeds2FA(false);
    setTfaCode('');
    setError('');
  };

  // Handler for Google login success
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      await onLogin(credentialResponse.credential, null, true);
    } catch (err) {
      setError('Google Sign-In failed. Please try again.');
    }
  };

  // Google login — sign-in only. Only the three non-sensitive scopes
  // (openid / email / profile) are requested here, so Google does NOT
  // show the "Google hasn't verified this app" warning to ordinary
  // users. Restricted scopes (Gmail, Chat, Drive, Sheets, Docs, Calendar,
  // Contacts) are requested separately from Settings > Google Workspace
  // where an admin clicking "Connect" understands what's being granted.
  const googleCodeLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: window.location.origin + '/login',
    scope: 'openid email profile',
  });

  // Handle Google redirect callback — check URL for auth code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code && onLogin) {
      setError('Signing in with Google...');
      // Remove code from URL
      window.history.replaceState({}, '', '/login');
      api.post('/auth/google-code', { code, redirect_uri: window.location.origin + '/login' })
        .then(res => {
          setError('');
          return onLogin(res.data.access_token, null, false);
        })
        .catch(err => {
          console.error('Google auth error:', err);
          // Distinguish three failure modes so the user can act:
          //   1. Server returned an error with `detail` — show the detail (e.g. Google token exchange failed)
          //   2. Server returned an error WITHOUT detail — show status code so it's not silent
          //   3. No response at all — server was down or network blocked the call
          let msg;
          if (err?.response?.data?.detail) {
            const d = err.response.data.detail;
            msg = typeof d === 'string' ? d : JSON.stringify(d);
          } else if (err?.response?.status) {
            msg = `server returned HTTP ${err.response.status}`;
          } else if (err?.message) {
            msg = `couldn't reach the server (${err.message})`;
          } else {
            msg = 'Unknown error';
          }
          setError(`Google Sign-In failed: ${msg}`);
        });
    }
  }, [onLogin]);

  // Handler for Google login error
  const handleGoogleError = () => {
    setError('Google Sign-In failed. Please check your account settings.');
  };

  return (
    <div className="login-page">
      <div className={`login-scene${brand.photo ? ' has-photo' : ''}`}
           style={brand.photo ? { '--login-photo': `url(${brand.photo})` } : undefined} />

      {brand.eyebrow && (
        <div className="login-eyebrow">
          {brand.eyebrow.split(/[,|]/).map(w => w.trim()).filter(Boolean)
            .map(w => <span key={w}>{w}</span>)}
        </div>
      )}
      {brand.strapline && <div className="login-strapline">{brand.strapline}</div>}

      <div className="login-container">
        <div className="login-left">
          <div className="login-brand">
            <div className="brand-icon">
              {brand.logo
                ? <img src={brand.logo} alt={brand.name}
                       className={brand.logoInvert ? 'brand-logo-invert' : undefined} />
                : <TestTube2 size={44} />}
            </div>
            {/* A logo that already carries the name should not have the name
                printed beside it — most laboratory marks ARE the wordmark. */}
            <div className="brand-wordmark">
              {!brand.logo && <h1 className="brand-title">{brand.name}</h1>}
              <p className="brand-subtitle">Laboratory Information System</p>
            </div>
          </div>

          {/* The headline may pick one word out in the accent colour, written
              as <span> in the setting. Only <span>, <b> and <em> survive — a
              setting is not a place to put markup the page has to trust. */}
          {brand.headline
            ? <h2 className="login-headline"
                  dangerouslySetInnerHTML={{ __html: safeHeadline(brand.headline) }} />
            : <h2 className="login-headline">{brand.name}</h2>}
          {brand.sub && <p className="login-sub">{brand.sub}</p>}

          <div className="login-features">
            <div className="feature-item">
              <div className="feature-icon"><FileText size={26} /></div>
              <div className="feature-text">
                <h3>Complete Lab Management</h3>
                <p>Manage orders, results, and workflows efficiently.</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><BarChart3 size={26} /></div>
              <div className="feature-text">
                <h3>Real-time Analytics</h3>
                <p>Monitor performance with comprehensive dashboards.</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><Shield size={26} /></div>
              <div className="feature-text">
                <h3>Secure &amp; Compliant</h3>
                <p>HIPAA-compliant with enterprise-grade security.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="login-card">
            {/* ---- 2FA Verification Step ---- */}
            {needs2FA ? (
              <>
                <div className="login-header">
                  <div className="tfa-icon-wrap">
                    <ShieldCheck size={40} className="tfa-icon" />
                  </div>
                  <h2>Two-Factor Verification</h2>
                  <p>Enter the 6-digit code sent to your phone</p>
                </div>

                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleVerify2FA} className="login-form">
                  <TwoFactorCodeInput
                    value={tfaCode}
                    onChange={setTfaCode}
                    disabled={isLoading}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                    loading={isLoading}
                    disabled={isLoading || tfaCode.length < 6}
                  >
                    Verify Code
                  </Button>
                </form>

                <div className="tfa-actions">
                  <button
                    className="tfa-resend-btn"
                    onClick={handleResendCode}
                    disabled={resendCountdown > 0}
                  >
                    {resendCountdown > 0
                      ? `Resend code in ${resendCountdown}s`
                      : 'Resend code'}
                  </button>
                  <button className="tfa-back-btn" onClick={handleBack}>
                    Back to login
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* ---- Normal Login Form ---- */}
                <div className="login-header">
                  <h2>Welcome Back</h2>
                  <p>Sign in to your account to continue</p>
                </div>

                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                  <div className="form-group">
                    <label htmlFor="username">Email or username</label>
                    <div className="input-wrapper">
                      <Mail size={18} className="input-icon" />
                      <input
                        id="username"
                        type="text"
                        placeholder="Email or username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                      <button type="button" className="input-reveal"
                              onClick={() => setShowPassword(v => !v)}
                              aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-options">
                    <label className="checkbox-label">
                      <input type="checkbox" />
                      <span>Remember me</span>
                    </label>
                    <a
                      href="#forgot"
                      className="forgot-link"
                      onClick={(e) => { e.preventDefault(); setShowForgot(true); setForgotIdentifier(username || ''); setForgotMsg(''); }}
                    >
                      Forgot password?
                    </a>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    Sign In <ArrowRight size={18} style={{ marginLeft: 8 }} />
                  </Button>
                </form>

                <div className="login-divider">or continue with</div>

                <div className="google-signin-section">
                  {/* Google's own button on the PATIENT-PORTAL client.
                      The previous auth-code button was bound to the staff
                      client, whose project carries Gmail scopes — so every
                      patient using Google here met "Google hasn't verified
                      this app". This client's project declares only
                      openid/email/profile, and /auth/google now accepts both
                      audiences, so the one button serves both audiences. */}
                  {/* Google renders its button inside a cross-origin iframe whose
                      canvas is white, so wherever the dark pill's rounded corners
                      cut away, that white shows through as a frame. The iframe's
                      interior cannot be styled from here — so the wrapper is
                      clipped to the same pill shape, which removes the white
                      instead of trying to recolour it. */}
                  <div className="login-oauth">
                  <div className="google-login-button-wrapper"
                       style={{ display: 'flex', justifyContent: 'center',
                                borderRadius: 999, overflow: 'hidden',
                                background: 'transparent' }}>
                    <GoogleOAuthProvider clientId={SIGNIN_GOOGLE_CLIENT_ID}>
                      <GoogleLogin
                        onSuccess={async (cred) => {
                          setError(''); setIsLoading(true);
                          try {
                            const r = await universalGoogle(cred.credential);
                            if (r.kind === 'patient') {
                              sessionStorage.setItem('portal_token', r.data.access_token);
                              window.location.assign('/portal');
                              return;
                            }
                            const resp = await api.post('/auth/google', { token: cred.credential });
                            await onLogin(resp.data.access_token, resp.data.refresh_token);
                          } catch (e2) {
                            setError(e2?.response?.data?.detail
                                     || 'That Google account is not registered here.');
                          } finally { setIsLoading(false); }
                        }}
                        onError={() => setError(
                          // Google shows "Error 400: origin_mismatch" on its own
                          // page and the app used to say only that sign-in "did
                          // not complete", which sends the reader looking for a
                          // fault in the app. The cause is always the same and
                          // it is fixable in about a minute — but only if the
                          // message says which origin and which client.
                          `Google sign-in is not enabled for ${window.location.origin}. `
                          + `An administrator needs to add that exact address to the `
                          + `OAuth client's Authorized JavaScript origins (and `
                          + `${window.location.origin}/login to its Authorized redirect URIs) `
                          + `in the Google Cloud console. Client: ${SIGNIN_GOOGLE_CLIENT_ID || 'not configured'}. `
                          + `Signing in with a username and password works regardless.`)}
                        theme="filled_black" shape="pill" width="320" />
                    </GoogleOAuthProvider>
                  </div>
                  {APPLE_SERVICES_ID && (
                    <div className="apple-login-button-wrapper">
                      <button
                        type="button"
                        onClick={handleAppleSignIn}
                        disabled={isLoading}
                        style={{
                          width: '100%', padding: '10px 16px', border: '1px solid #000', borderRadius: 8,
                          background: '#000', color: '#fff', cursor: isLoading ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                          fontSize: 14, fontWeight: 500, opacity: isLoading ? 0.7 : 1,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                          <path d="M17.05 12.536c-.018-2.017 1.641-2.986 1.714-3.035-.934-1.365-2.39-1.55-2.905-1.573-1.235-.127-2.414.728-3.043.728-.63 0-1.6-.71-2.633-.69-1.354.02-2.606.787-3.302 1.995-1.407 2.442-.36 6.054 1.015 8.034.675.97 1.481 2.062 2.539 2.022 1.018-.04 1.404-.66 2.637-.66 1.233 0 1.578.66 2.66.64 1.1-.02 1.794-.99 2.465-1.962.777-1.128 1.099-2.222 1.118-2.279-.024-.01-2.144-.822-2.165-3.22zM15.09 6.837c.56-.68.938-1.622.835-2.562-.807.033-1.784.538-2.365 1.214-.52.603-.976 1.563-.853 2.485.9.07 1.82-.458 2.383-1.137z"/>
                        </svg>
                        Sign in with Apple
                      </button>
                    </div>
                  )}
                  </div>
                  <p className="google-signin-note">
                    Use the Google account your laboratory has on file.
                  </p>

                  {/* No "are you a patient?" link. This page signs BOTH in and
              routes them; offering a second door is what made people
              pick the wrong one. */}
                </div>
              </>
            )}

            <div className="login-footer">
              <p>
                Don't have an account?{' '}
                <a href="/register">Register here</a>
                {' '}|{' '}
                <a href="mailto:admin@system.com?subject=Account%20Request">Contact Administrator</a>
              </p>
            </div>

            <div className="login-assurance">
              <span>Secure</span><span>Reliable</span><span>Always on</span>
            </div>
          </div>

          <div className="login-info">
            <p>&copy; 2026 {brand.name}. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div
          onClick={() => !forgotBusy && setShowForgot(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', color: '#111827', borderRadius: 14, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Reset your password</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Enter your email or username. We'll email you a new temporary password — sign in with it and then change it from <b>My Account</b>.
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Email or Username</label>
            <input
              type="text"
              value={forgotIdentifier}
              onChange={(e) => setForgotIdentifier(e.target.value)}
              disabled={forgotBusy}
              placeholder="you@example.com"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && forgotIdentifier.trim() && !forgotBusy) {
                  e.preventDefault();
                  document.getElementById('forgot-submit-btn')?.click();
                }
              }}
            />

            {forgotMsg && (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: '#ecfdf5', color: '#065f46', fontSize: 13, marginBottom: 14, border: '1px solid #a7f3d0' }}>
                {forgotMsg}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowForgot(false)}
                disabled={forgotBusy}
                style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: forgotBusy ? 'wait' : 'pointer' }}
              >
                Close
              </button>
              <button
                id="forgot-submit-btn"
                type="button"
                disabled={forgotBusy || !forgotIdentifier.trim()}
                onClick={async () => {
                  setForgotBusy(true);
                  try {
                    const base = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? `${window.location.protocol}//${window.location.host}` : `http://${window.location.hostname}:8000`);
                    const res = await fetch(`${base}/auth/forgot-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ identifier: forgotIdentifier.trim() }),
                    });
                    const data = await res.json().catch(() => ({}));
                    setForgotMsg(data.detail || 'If an account matches, an email has been sent.');
                  } catch {
                    setForgotMsg('If an account matches, an email has been sent.');
                  } finally {
                    setForgotBusy(false);
                  }
                }}
                style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: forgotBusy ? 0.7 : 1 }}
              >
                {forgotBusy ? 'Sending...' : 'Email me a new password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
